import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons/faCircleInfo";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Box, CardContent, Grid, IconButton, Pagination, Stack, Theme, Typography } from '@mui/material';
import { APP_DEFAULT_EXPORT_PAGE_SIZE, APP_PAGE_SIZE_DEFAULT } from 'app/appConstants';
import { AppImageDimension } from "app/appImages";
import { AxiosResponse } from "axios";
import { MprcCard, MprcCardActions, MprcContainer, MprcIconButtonMoreOptionsDropdown, MprcInformationDialog, MprcJoinComponents, MprcLink, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { MprcFilterCount } from 'components/filter';
import { exportToXLSX } from "components/MprcExportData";
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { EnrollmentHistoryDto, EnrollmentHistoryDtoPaginatedList, EnrollStatus, HistoryOrderBy } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from 'providers/SessionProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from "react-router";
import { LmsPaths } from "routes/LmsRouter";
import { IAdvFilterParams } from "views/general/AdvancedFilter";
import { TrainingInfoDialog } from "views/lms/dialogs/TrainingInfoDialog";
import { EnrollmentDetailsDialog } from 'views/lms/monitoring/Supervisor/dialogs/EnrollmentDetailsDialog';
import { TrainingEditRating } from "views/lms/student/TrainingRatingView/TrainingEditRating";
import { ConclusionFormDialog } from '../EnrollmentView/ECourse/ConclusionFormDialog';
import { getEnrollmentCertificate } from "../EnrollmentView/Shared/EnrollmentActions";
import { TrainingRegistrationDialog } from '../Registration/TrainingRegistrationDialog';
import { Filter } from './HistoryFilter';

/////////////////////////////////////////////////////////////////////////////////////

export interface HistoryFilterOptions extends IAdvFilterParams<HistoryOrderBy> { }

type HistoryOption = "review" | "summary" | "certificate" | "reenroll" | "newConclusionForm" | "editConclusionForm" | "about" | "rating";

/////////////////////////////////////////////////////////////////////////////////////

function getStatusColor(status: EnrollStatus, theme: Theme) {
    return status == EnrollStatus.Approved
        ? theme.palette.success.main
        : status == EnrollStatus.Reproved
            ? theme.palette.error.main
            : status == EnrollStatus.Lapsed
                ? theme.palette.warning.main
                : theme.palette.cancel.dark;
}

/////////////////////////////////////////////////////////////////////////////////////

export const StudentHistory: React.FC = () => {

    const api = useCoreApi();
    const navigate = useNavigate();
    const { t } = useTranslation("lms");
    const { user: currentUser } = useSession();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dialog, setDialog] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [gettingEnrollment, setGettingEnrollment] = useState<boolean>(false);
    const [exporting, setExporting] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [viewData, setViewData] = useState<EnrollmentHistoryDtoPaginatedList>({
        items: [],
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalRows: 0
    });
    const [filter, setFilter] = useState<HistoryFilterOptions>({
        searchText: "",
        order: HistoryOrderBy.MostRecent,
        advFilters: []
    });

    const pageNumber = useRef<number>(1);

    const filtered = filter.searchText.length > 0 || filter.advFilters.length > 0;

    /////////////////////////////////////////////////////////////////////////////////////

    const getData = (pageNumber: number, pageSize: number): Promise<AxiosResponse<EnrollmentHistoryDtoPaginatedList, any>> => {
        return api.v1.lmsStudent.getHistory({
            searchText: filter.searchText ?? null,
            filter: filter.advFilters,
            orderBy: filter.order,
            pageNumber: pageNumber,
            pageSize: pageSize
        });
    };
    ///
    const handleFilter = (data: Partial<HistoryFilterOptions>) => {
        setFilter(state => ({ ...state, ...data }));
        pageNumber.current = 1;
        setLoading(true);
    };

    ///
    const resetFilter = () => {
        handleFilter({ searchText: "", advFilters: [] });
    };

    ///
    const handleClickItem = (item: EnrollmentHistoryDto) => {
        if (item.status == EnrollStatus.Approved || item.status == EnrollStatus.Reproved || item.status == EnrollStatus.Lapsed) {
            setDialog(
                <EnrollmentDetailsDialog
                    enrollmentId={item.enrollmentId}
                    subtitle={item.trainingName}
                    onClose={() => setDialog(null)} />
            );
        }
        else if (item.status == EnrollStatus.ConclusionFormRejected && item.classroomId) {
            setDialog(
                <ConclusionFormDialog
                    trainingId={item.trainingId}
                    classroomId={item.classroomId}
                    enrollmentId={item.enrollmentId}
                    onClose={() => setDialog(null)}
                />
            );
        }
        else {
            setDialog(
                <TrainingRegistrationDialog
                    trainingId={item.trainingId}
                    onClose={() => setDialog(null)}
                    readonly={!item.canReenroll}
                />
            );
        }
    };

    ///
    const handleExportXLS = async () => {

        setExporting(true);

        let dataRows = viewData.items;
        let fileName = (`${t("Student.History.Title")}-${currentUser.name}-${moment().toISOString()}`).replace(/([^a-z0-9\s]+)/gi, '-');

        if (viewData.totalPages > 1) {
            try {
                const { data } = await getData(1, APP_DEFAULT_EXPORT_PAGE_SIZE);
                dataRows = data.items;
            }
            catch (e) {
                MprcRequestSnackbarErrorReport(e);
            }
        }

        let csv = dataRows.map((v) => {
            const row = {} as any;
            row[t("Student.History.Export.Date")] = v.actionDate;
            row[t("Student.History.Export.TrainingName")] = v.trainingName;
            row[t("Student.History.Export.ClassName")] = v.className;
            row[t("Student.History.Export.Status")] = v.statusName;
            row[t("Student.History.Export.ContentType")] = v.trainingContentTypeName;
            row[t("Student.History.Export.IsMandatory")] = v.isMandatory ? t("shared:Literal.Yes") : t("shared:Literal.No");
            row[t("Student.History.Export.Expired")] = v.status == EnrollStatus.Lapsed ? t("shared:Literal.Yes") : t("shared:Literal.No");
            row[t("Student.History.Export.Score")] = v.scorePercent ?? "";
            row[t("Student.History.Export.ExpireDate")] = v.certificateValidUntil ?? "";

            return row;
        });
        if (csv.length > 0) exportToXLSX(csv, fileName);

        setExporting(false);

    };

    ///
    const getMenuOptions = useCallback((item: EnrollmentHistoryDto): MprcDropdownItems<HistoryOption> => {
        var group0: MprcDropdownItems<HistoryOption> = [];
        var group1: MprcDropdownItems<HistoryOption> = [];
        var group2: MprcDropdownItems<HistoryOption> = [];

        if (item.canRating)
            group0.push({ text: t("Student.History.RatingAction"), value: "rating" });

        if (item.canReview)
            group0.push({ text: t("Student.History.ReviewAction"), value: "review" });

        if (item.status == EnrollStatus.Approved || item.status == EnrollStatus.Reproved || item.status == EnrollStatus.Lapsed)
            group0.push({ text: t("Student.History.SummaryAction"), value: "summary" });

        if (item.certificateFileId != null)
            group0.push({ text: t("Student.History.CertificateAction"), value: "certificate" });

        /////

        if (item.canReportNewConclusionForm)
            group1.push({ text: t("Student.History.ReportNewConclusionFormAction"), value: "newConclusionForm" });
        else if (item.canReenroll)
            group1.push({ text: t("Student.History.ReenrollAction"), value: "reenroll" });

        if (item.status == EnrollStatus.ConclusionFormRejected)
            group1.push({ text: t("Student.History.ConclusionFormAction"), value: "editConclusionForm" });

        /////
        group2.push({ text: t("Student.History.AboutAction"), value: "about" });


        return MprcDropdownBuildGroupedItems<HistoryOption>([group0, group1, group2]);
    }, []);

    ///
    const handleSelectMenuOption = (option: HistoryOption, item: EnrollmentHistoryDto) => {
        switch (option) {
            case "about":
                setDialog(
                    <TrainingInfoDialog
                        trainingId={item.trainingId}
                        onClose={() => setDialog(null)}
                    />
                );
                break;

            case "rating":
                setDialog(
                    <TrainingEditRating
                        enrollmentId={item.enrollmentId}
                        trainingName={item.trainingName}
                        onClose={() => setDialog(null)}
                    />
                );
                break;

            case "review":
                navigate(LmsPaths.BuildEnrollmentViewer(item.trainingType, item.enrollmentId));
                break;

            case "summary":
                setDialog(
                    <EnrollmentDetailsDialog
                        enrollmentId={item.enrollmentId}
                        subtitle={item.trainingName}
                        onClose={() => setDialog(null)}
                    />
                );
                break;

            case "certificate":
                getEnrollmentCertificate(item.certificateFileId!);
                break;

            case "reenroll":
                setDialog(
                    <TrainingRegistrationDialog
                        trainingId={item.trainingId}
                        onClose={() => setDialog(null)}
                        readonly={!item.canReenroll}
                    />
                );
                break;

            case "newConclusionForm":
            case "editConclusionForm":
                if (item.classroomId) {
                    setDialog(
                        <ConclusionFormDialog
                            trainingId={item.trainingId}
                            classroomId={item.classroomId}
                            enrollmentId={option == "editConclusionForm" ? item.enrollmentId : null}
                            onClose={() => setDialog(null)}
                        />
                    );
                }
                break;
        }
    };

    /////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (loading) {
            getData(pageNumber.current, APP_PAGE_SIZE_DEFAULT)
                .then(({ data }) => setViewData(data))
                .catch(e => setViewError(e))
                .finally(() => setLoading(false));
        }
    }, [loading]);

    useEffect(() => {
        if (searchParams.has("e")) {
            var enrollmentId = searchParams.get("e");
            if (!enrollmentId) return;

            setGettingEnrollment(true);
            api.v1.lmsStudent.getHistoryItem({ enrollmentId })
                .then(response => {
                    var item = response.data;
                    if (item != null && response.status == 200) {
                        if (item.status == EnrollStatus.Approved || item.status == EnrollStatus.Reproved || item.status == EnrollStatus.Lapsed) {
                            handleSelectMenuOption("summary", item);
                        }
                        else {
                            enqueueSnackbar(t("Student.History.EnrollmentNotSupportSummaryText"), { variant: "error" });
                        }
                    }
                    setSearchParams("");
                })
                .catch(e => MprcRequestSnackbarErrorReport(e))
                .finally(() => setGettingEnrollment(false));
        }
    }, [searchParams]);

    /////////////////////////////////////////////////////////////////////////////////////

    if (viewError)
        return <MprcRequestErrorReport error={viewError} />;

    return (
        <>
            {dialog}

            <MprcRequestIndicator visible={loading || exporting || gettingEnrollment} type={loading || gettingEnrollment ? "request" : "post"} />

            <MprcContainer>
                <MprcCard>
                    <Filter
                        totalRows={viewData.totalRows}
                        filterOptions={filter}
                        onFilterChange={handleFilter}
                        onExportXLS={handleExportXLS}
                    />
                    <CardContent>
                        <MprcContainer>
                            <MprcFilterCount
                                loading={loading}
                                isEmpty={viewData.totalRows == 0}
                                countText={t("Student.History.RecordsFoundText", { count: viewData.totalRows })}
                                filter={{
                                    active: filtered,
                                    text: t("Student.History.RecordsFilteredText", { count: viewData.totalRows }),
                                    onResetFilter: resetFilter
                                }}
                            />
                        </MprcContainer>
                        <Stack direction="column" spacing={1}>
                            {viewData.items.map(item => {
                                const menuItems = getMenuOptions(item);
                                return (
                                    <Grid key={item.enrollmentId} container direction="row" spacing={1} alignItems="center">
                                        <Grid>
                                            <Box
                                                sx={theme => ({
                                                    backgroundColor: getStatusColor(item.status, theme),
                                                    width: 6,
                                                    borderRadius: 4,
                                                    height: 67
                                                })}
                                            />
                                        </Grid>
                                        <Grid>
                                            <MprcLink onClick={() => handleClickItem(item)}>
                                                <Avatar
                                                    variant="rounded"
                                                    sx={{ ...AppImageDimension.ThumbnailMd }}
                                                    src={item.trainingThumbnailUrl}
                                                />
                                            </MprcLink>
                                        </Grid>
                                        <Grid>
                                            <Stack direction="column">
                                                <Stack direction="row" alignItems="center" sx={{ mb: -0.5 }}>
                                                    <MprcListItemTitle onClick={() => handleClickItem(item)}>
                                                        {item.trainingName}
                                                    </MprcListItemTitle>
                                                    {menuItems.length > 0 &&
                                                        <MprcIconButtonMoreOptionsDropdown
                                                            color="muted"
                                                            onChange={(selection) => handleSelectMenuOption(selection, item)}
                                                            items={menuItems}
                                                        />
                                                    }
                                                </Stack>

                                                <Typography variant="caption">
                                                    <MprcJoinComponents
                                                        separator="&bull;"
                                                        components={[
                                                            item.trainingContentTypeName,
                                                            item.isMandatory ? t("Training.Literals.TrainingMandatoryText") : null,
                                                            item.multipleClasses ? item.className : null,
                                                            item.masterEnrollmentTrainingName != null ? t("shared:SysObjects.LearningPath") + ": " + item.masterEnrollmentTrainingName : null
                                                        ]}
                                                    />
                                                </Typography>
                                                <div>
                                                    <Typography
                                                        variant="overline"
                                                        component="span"
                                                        sx={{ color: (theme) => getStatusColor(item.status, theme) }}
                                                    >
                                                        {item.statusName}
                                                    </Typography>
                                                    &nbsp;
                                                    <Typography
                                                        variant="h6"
                                                        color="muted.main"
                                                        component="span"
                                                    >
                                                        <MprcJoinComponents
                                                            separator="&bull;"
                                                            components={
                                                                [
                                                                    <>
                                                                        {t("shared:Literal.At")}&nbsp;{Formaters.dayMonth(item.actionDate)}
                                                                        {item.actionRemarks != null &&
                                                                            <>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    title={`${t("Student.History.ReasonText")}: ${item.actionRemarks}`}
                                                                                    onClick={() => setDialog(<MprcInformationDialog width="sm" message={`${t("Student.History.ReasonText")}: ${item.actionRemarks}`} onClose={() => setDialog(null)} />)}>
                                                                                    <FontAwesomeIcon icon={faCircleInfo} />
                                                                                </IconButton>
                                                                            </>}
                                                                    </>,
                                                                    item.scorePercent != null ? t("Student.History.ScoreText", { score: item.scorePercent }) : null,
                                                                    (item.status == EnrollStatus.Approved && item.certificateValidUntil != null) ? t("Student.History.ValidUntilText", { date: Formaters.dayMonth(item.certificateValidUntil) }) : null,
                                                                    (item.status == EnrollStatus.Lapsed && item.certificateValidUntil != null) ? <Typography variant="overline" color="warning.main">{t("Student.History.CertificateExpiredText", { date: Formaters.dayMonth(item.certificateValidUntil) })}</Typography> : null
                                                                ]
                                                            }
                                                        />
                                                    </Typography>
                                                </div>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                );
                            }
                            )}
                        </Stack>
                    </CardContent>
                    <MprcCardActions>
                        {viewData && viewData.totalPages > 1 && (
                            <Pagination
                                shape="rounded"
                                color="primary"
                                page={viewData.pageNumber}
                                count={viewData.totalPages}
                                onChange={(e, pg) => {
                                    if (pg != pageNumber.current) {
                                        pageNumber.current = pg;
                                        setLoading(true);
                                    }
                                }}
                            />
                        )}
                    </MprcCardActions>
                </MprcCard>
            </MprcContainer>
        </>
    );
};
